import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { UserPlus, Check } from "lucide-react";
import { toast } from "sonner";

const EMPTY_OFFER = { title: "", description: "", discount_code: "", expires_at: "" };

export default function ReferralProgramCard({ marketingActive }) {
  const [program, setProgram] = useState({
    active: false,
    referrer_offer: { ...EMPTY_OFFER },
    referee_offer: { ...EMPTY_OFFER },
  });
  const [referrals, setReferrals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [pgm, refs] = await Promise.all([
        api.get("/me/business/referral-program"),
        api.get("/me/business/referrals"),
      ]);
      if (pgm?.data) setProgram(pgm.data);
      if (refs?.data?.items) setReferrals(refs.data.items);
    } catch (e) {
      console.warn("ReferralProgramCard load failed:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateOffer = (side, k) => (e) => {
    setProgram({ ...program, [side]: { ...program[side], [k]: e.target.value } });
  };

  const save = async () => {
    if (!marketingActive) {
      toast.error("The Marketing add-on ($35/mo) is required to run a referral program.");
      return;
    }
    if (!program.referrer_offer.title || !program.referee_offer.title) {
      toast.error("Both offers need a title.");
      return;
    }
    if (!program.referrer_offer.description || !program.referee_offer.description) {
      toast.error("Both offers need a description.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.put("/me/business/referral-program", program);
      setProgram(data.referral_program);
      toast.success("Referral program saved");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const markRedeemed = async (id) => {
    const notes = window.prompt("Optional note about this redemption (e.g. visited 10/14):") || "";
    try {
      const { data } = await api.post(`/me/business/referrals/${id}/mark-redeemed`, { notes });
      setReferrals((rs) => rs.map((r) => (r.id === id ? data.referral : r)));
      toast.success("Referral marked as redeemed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const offerFields = (side, label) => (
    <div className="space-y-3" data-testid={`referral-offer-${side}`}>
      <div className="flex items-center gap-2">
        <h4 className="font-semibold text-gray-900">{label}</h4>
      </div>
      <div>
        <Label htmlFor={`${side}-title`} className="text-xs">Offer title</Label>
        <Input id={`${side}-title`} value={program[side].title} onChange={updateOffer(side, "title")} placeholder={side === "referrer_offer" ? "10% off your next visit" : "20% off your first visit"} className="rounded-md mt-1" data-testid={`referral-${side}-title`} />
      </div>
      <div>
        <Label htmlFor={`${side}-desc`} className="text-xs">Description</Label>
        <Textarea id={`${side}-desc`} value={program[side].description} onChange={updateOffer(side, "description")} rows={3} placeholder="What the customer gets, how to redeem, any fine print" className="rounded-md mt-1" data-testid={`referral-${side}-desc`} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor={`${side}-code`} className="text-xs">Discount code (optional)</Label>
          <Input id={`${side}-code`} value={program[side].discount_code || ""} onChange={updateOffer(side, "discount_code")} placeholder="WELCOME20" className="rounded-md mt-1 font-mono uppercase" data-testid={`referral-${side}-code`} />
        </div>
        <div>
          <Label htmlFor={`${side}-exp`} className="text-xs">Expires (optional)</Label>
          <Input id={`${side}-exp`} type="date" value={program[side].expires_at || ""} onChange={updateOffer(side, "expires_at")} className="rounded-md mt-1" data-testid={`referral-${side}-exp`} />
        </div>
      </div>
    </div>
  );

  return (
    <Card className="rounded-md border border-border p-6" data-testid="referral-program-card">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <UserPlus size={20} className="text-[#B31E1E]" />
          <h3 className="font-display text-xl">Referral program</h3>
          {program.active && marketingActive && (
            <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 rounded-sm">Live</Badge>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Active</span>
          <Switch
            checked={!!program.active}
            onCheckedChange={(v) => setProgram({ ...program, active: v })}
            disabled={!marketingActive}
            data-testid="referral-active-toggle"
          />
        </div>
      </div>

      {!marketingActive && (
        <div className="mb-4 p-3 rounded-sm bg-amber-50 border border-amber-200 text-sm text-amber-900" data-testid="referral-marketing-required">
          The <strong>Marketing add-on</strong> ($35/mo) is required to run a referral program. Upgrade from your subscription page to unlock it.
        </div>
      )}

      <p className="text-sm text-gray-600 leading-relaxed mb-4 max-w-2xl">
        Existing customers — anyone who has reviewed you, been reviewed by you, or been added to your roster — can send a friend an offer. Set up two offers: one for the new customer, one as a thank-you to the existing customer once their referral is claimed.
      </p>

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : (
        <>
          <div className="grid md:grid-cols-2 gap-6">
            {offerFields("referee_offer", "Welcome offer for the new customer")}
            {offerFields("referrer_offer", "Thank-you reward for the existing customer")}
          </div>
          <div className="mt-6 flex gap-3">
            <Button onClick={save} disabled={saving || !marketingActive} className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717] h-10 px-5" data-testid="referral-save-button">
              {saving ? "Saving…" : "Save program"}
            </Button>
          </div>
        </>
      )}

      {referrals.length > 0 && (
        <div className="mt-8">
          <h4 className="font-semibold mb-3">Referrals received ({referrals.length})</h4>
          <ul className="space-y-2" data-testid="referral-list">
            {referrals.map((r) => (
              <li key={r.id} className="flex items-center justify-between border border-border rounded-sm px-4 py-3" data-testid={`referral-row-${r.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {r.referrer_name || "Someone"} → {r.referee_name} <span className="font-normal text-gray-500">({r.referee_email})</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    Code <span className="font-mono">{r.code}</span> · Status{" "}
                    <span className={
                      r.status === "redeemed" ? "text-emerald-700 font-semibold" :
                      r.status === "claimed" ? "text-[#B31E1E] font-semibold" : "text-gray-700"
                    }>
                      {r.status}
                    </span>
                    {r.redemption_method && <> · {r.redemption_method}</>}
                  </div>
                </div>
                {r.status !== "redeemed" && (
                  <Button size="sm" variant="outline" onClick={() => markRedeemed(r.id)} className="rounded-sm shrink-0 ml-3" data-testid={`mark-redeemed-${r.id}`}>
                    <Check size={14} className="mr-1" />Mark redeemed
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}
